import { ChatUserModel } from 'types/chat-users/chat-user';
import { MessageMediaType, MessageStatus } from 'types/chat/messages/common';
import { audioExtensionRegex } from './regexes/media';
import { TemplateVarsData } from 'types/templates/templates-menu';

import LocationOnIcon from '@mui/icons-material/LocationOn';
import MicIcon from '@mui/icons-material/Mic';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import VideoCameraBackIcon from '@mui/icons-material/VideoCameraBack';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import DescriptionIcon from '@mui/icons-material/Description';
import DoneIcon from '@mui/icons-material/Done';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { IsJsonString, safeParseJSON } from './helpers';
import { intlGlobal } from './locales_old/intlGlobal';
import { IntlShape } from 'react-intl';
import { store } from 'store';
import { getPhoneSearchVariants } from 'utils/contact-search-filters';
import { doTagsMatchLabelFilterTokens, tagNameFromLabelFilterToken } from 'utils/conversation-tag-filter';
import { isOutgoingLastMessageFailure, OUTGOING_NON_FAILURE_STATUSES } from 'utils/whatsappErrors';
import { migrateCustomView, isFilterGroup } from 'utils/migrate-custom-views';

//* Chat users
function doesChatUserMatchSearchQuery(currentUser: ChatUserModel, searchQuery: string): boolean {
  const q = searchQuery.trim().toLowerCase();
  if (!q) return true;

  const haystacks = [
    currentUser.FullName,
    currentUser.FirstName,
    currentUser.LastName,
    currentUser.ContactFullName,
    currentUser.WhatsappFullName,
    currentUser.ComposedName,
    currentUser.SearchIndexName,
    currentUser.UserDisplayPhoneNumber,
    currentUser.Email,
  ]
    .filter(Boolean)
    .map((s) => String(s).toLowerCase());

  if (haystacks.some((h) => h.includes(q))) return true;

  const phoneVariants = getPhoneSearchVariants(searchQuery).map((v) => v.toLowerCase());
  return haystacks.some((h) => phoneVariants.some((v) => h.includes(v)));
}

export function doesUserMatchCurrentFilters(currentUser: ChatUserModel, view: string) {
  console.log('currentUser', currentUser);
  
  const storeState = store.getState();
  const { searchQuery, label, labels, open, isOnlyUnread, filters } = storeState.chat.chatUsersFilters;

  //* Independent filters
  const searchCond = !searchQuery?.length ? true : doesChatUserMatchSearchQuery(currentUser, searchQuery);
  //* פילטר תגיות שיחה — OR בין טוקנים, מותאם לשדה ConversationTags (לא לתגית איש הקשר הישנה)
  const labelFilterTokens = labels && labels.length > 0 ? labels : label ? [label] : [];
  const labelCond = doTagsMatchLabelFilterTokens(currentUser.ConversationTags, labelFilterTokens);
  //* פתוחה/סגורה — סטטוס הפלואו הוא הסטטוס היחיד (סגורה ⇔ 6); IsOpen הישן יצא משימוש (27.10)
  const openCond =
    open === 'open'
      ? currentUser.flowStatusCode !== 6
      : open === 'close'
        ? currentUser.flowStatusCode === 6
        : true;
  const onlyUnreadCond = isOnlyUnread ? (currentUser.UnreadMessagesNumber ?? 0) > 0 : true;

  //* Filters, related to views
  let assigneeFilter: Array<string | null> = [];
  if (view === 'notMyChatUsers') {
    assigneeFilter = storeState.applicationUsers
      .filter((user) => {
        const userProfileConfig = safeParseJSON(user.ProfileConfig);

        const visible =
          userProfileConfig?.funnerInfo?.visibleInList !== undefined &&
          userProfileConfig?.funnerInfo?.visibleInList !== null
            ? userProfileConfig.funnerInfo.visibleInList
            : user.IsActive;

        return visible && storeState.user!.Udid !== user.Udid;
      })
      .map((user) => user.Udid);
  }

  //* Bots users view
  const isBot = view === 'botsChatUsers';

  //* My users view
  const isMyCond =
    view === 'myChatUsers' || view === 'mySnoozedChatUsers'
      ? currentUser.NotificationContactUser === storeState.user?.Udid
      : true;

  const snoozedViewCond =
    view === 'mySnoozedChatUsers'
      ? currentUser.flowStatusCode === 5
      : view === 'myChatUsers'
        ? currentUser.flowStatusCode !== 5
        : true;

  //* יישור לפילטרי השרת (getChatUsers): "הסתיימו" נמשכת עם flowStatusCode=6
  //* ו"לא משויכות" עם flowStatusCode=1 — בלי התנאים האלה שיחה שהסתיימה/שויכה
  //* הייתה מוכנסת-מחדש לתצוגה בבנייה-מחדש על אירוע סוקט ונשארת שם עד רענון.
  const flowViewCond =
    view === 'completedChatUsers'
      ? currentUser.flowStatusCode === 6
      : view === 'unassignedChatUsers'
        ? currentUser.flowStatusCode === 1
        : true;

  //* תצוגה מותאמת — הערכה חלקית של קבוצות הפילטרים של התצוגה הפעילה (סטטוס
  //* פלואו / משויך-ל / תגית שיחה); תנאים אחרים נשארים בסינון השרת.
  const customViewCond =
    view === 'customChatUsers' ? doesUserMatchActiveCustomViewGroups(currentUser) : true;

  const assigneeFilterCond = assigneeFilter.length
    ? assigneeFilter.includes(currentUser.NotificationContactUser ?? '')
    : view === 'unassignedChatUsers'
      ? !currentUser.NotificationContactUser
      : true;
  // הכרעת מוצר 8.10: שיחת בוט מופיעה בכל התצוגות לפי הסטטוס שלה כרגיל — הסינון
  // הישן שהסתיר אותה מהתצוגות הרגילות (והעלים שיחות מהרשימה בכל עדכון סוקט)
  // בוטל; רק תצוגת «בוטים» מסננת, והיא דורשת בוט פעיל.
  const botCond = isBot ? Boolean(currentUser.isBot) : true;

  //* Additional filters (custom views)
  const additionalFiltersCond = doesUserMatchAdditionalFilters(filters ?? [], currentUser);

  return (
    searchCond &&
    labelCond &&
    openCond &&
    onlyUnreadCond &&
    assigneeFilterCond &&
    botCond &&
    isMyCond &&
    snoozedViewCond &&
    flowViewCond &&
    customViewCond &&
    additionalFiltersCond
  );
}

//* ===== הערכה חלקית של פילטרי תצוגה מותאמת (FilterGroup) בצד הלקוח =====
//* נדרשת כדי ששיחה תיכנס/תצא מתצוגה מותאמת בזמן אמת על אירוע סוקט (שיוך /
//* סטטוס פלואו / תגית). הסינון המלא נשאר בשרת; כאן מוערכים רק השדות שמשתנים
//* באירועים החיים. הכלל שמרני: תנאי שאינו נתמך לא מכריע — שורה לא תוסר
//* מהרשימה בגלל תנאי שאיננו יודעים להעריך.

type CustomViewConditionLike = {
  field?: string;
  operator?: string;
  value?: unknown;
};

//* true/false = הוכרע; null = שדה/אופרטור לא נתמך בצד הלקוח
function evalSupportedCustomCondition(cond: CustomViewConditionLike, user: ChatUserModel): boolean | null {
  const op = cond.operator;
  const values: string[] = Array.isArray(cond.value)
    ? (cond.value as unknown[]).map((v) => String(v))
    : cond.value != null
      ? [String(cond.value)]
      : [];

  const evalScalar = (actual: string | null | undefined): boolean | null => {
    const has = actual != null && String(actual).length > 0;
    switch (op) {
      case 'eq':
      case 'in':
        return has && values.includes(String(actual));
      case 'ne':
      case 'notin':
        return !(has && values.includes(String(actual)));
      case 'isnull':
        return !has;
      case 'isnotnull':
        return has;
      default:
        return null;
    }
  };

  switch (cond.field) {
    case 'lastSession.flowStatusCode':
      return evalScalar(user.flowStatusCode != null ? String(user.flowStatusCode) : null);
    case 'managerId':
    case 'lastSession.manager.id':
      return evalScalar(user.NotificationContactUser ?? null);
    case 'conversationTag': {
      const tags = (Array.isArray(user.ConversationTags) ? user.ConversationTags : []).map((t) =>
        String(t),
      );
      //* הערך עשוי להיות שם תגית גולמי או JSON של ChatUserLabel — אותו נרמול כמו בסינון הרשימה
      const tagValues = values
        .map((v) => tagNameFromLabelFilterToken(v))
        .filter((v): v is string => Boolean(v));
      switch (op) {
        case 'eq':
        case 'in':
        case 'contains':
          return tagValues.length === 0 ? null : tagValues.some((v) => tags.includes(v));
        case 'ne':
        case 'notin':
          return tagValues.length === 0 ? null : !tagValues.some((v) => tags.includes(v));
        case 'isnull':
          return tags.length === 0;
        case 'isnotnull':
          return tags.length > 0;
        default:
          return null;
      }
    }
    default:
      return null;
  }
}

export function doesUserMatchActiveCustomViewGroups(user: ChatUserModel): boolean {
  try {
    const state = store.getState();
    const viewId = state.chat.lastRequestedCustomViewId;
    if (!viewId) return true;
    const customView = state.userConfig.customViews.find((v) => v.id.toString() === viewId);
    if (!customView) return true;
    const migrated = migrateCustomView(customView);
    if (!migrated.filters.length || !isFilterGroup(migrated.filters[0])) return true;

    const groups = migrated.filters as Array<{ logic?: 'or' | 'and'; conditions: CustomViewConditionLike[] }>;
    //* תוצאת קבוצה: and — נכשלת אם תנאי נתמך נכשל; or — עוברת אם תנאי נתמך עבר,
    //* ונכשלת רק כשכל תנאיה נתמכים וכולם נכשלו (אחרת לא ניתן להכריע ⇒ עוברת).
    const groupResults = groups.map((group) => {
      const results = (group.conditions ?? []).map((cond) => evalSupportedCustomCondition(cond, user));
      if (group.logic === 'or') {
        if (results.some((r) => r === true)) return true;
        if (results.length > 0 && results.every((r) => r === false)) return false;
        return true;
      }
      return !results.some((r) => r === false);
    });

    if ((migrated.filtersLogic || 'and') === 'or') {
      return groupResults.length === 0 || groupResults.some(Boolean);
    }
    return groupResults.every(Boolean);
  } catch {
    //* הערכת פילטרים לעולם לא מפילה עדכון סוקט — במקרה ספק משאירים את השורה
    return true;
  }
}

export function doesUserMatchAdditionalFilters(filters: any[], user: ChatUserModel) {
  if (!filters || !user) return true;
  console.log('filters', filters);
  for (const filter of filters.filter((f) => f.value != null)) {
    const key = filter.key;
    let value = filter.value;

    console.log('key additional filters', key);
    console.log('value additional filters', value);
    console.log('user', user);
    switch (key) {
      case 'IsOpen':
        //* תצוגה ישנה (טרם המרה ב-DB): פתוחה = פלואו 1-5, סגורה = פלואו 6
        if ((user.flowStatusCode !== 6) !== Boolean(value)) {
          return false;
        }
        break;
      case 'IsOpenUpdateTimeFrom':
        if (user.OpenTime && new Date(user.OpenTime) <= new Date(value)) {
          return false;
        }
        break;
      case 'IsOpenUpdateTimeTo':
        if (user.OpenTime && new Date(user.OpenTime) >= new Date(value)) {
          return false;
        }
        break;
      case 'NotificationContactUser':
        if (!Array.isArray(value)) {
          value = [value];
        }
        if (!value.includes(user.NotificationContactUser)) {
          return false;
        }
        break;
      case 'LableName':
        console.log('user.LableName', user.LableName);
        console.log('value', value);
        if (user.LableName !== String(value)) {
          return false;
        }
        break;
      case 'LastMessageMediaType':
        if (user.LastMessageMediaType !== String(value)) {
          return false;
        }
        break;
      case 'LastMessageIsImage':
        if (value === true && user.LastMessageMediaType !== 'image') {
          return false;
        }
        break;
      case 'LastMessageIsVideo':
        if (value === true && user.LastMessageMediaType !== 'video') {
          return false;
        }
        break;
      case 'LastMessageIsAudio':
        if (value === true && user.LastMessageMediaType !== 'audio') {
          return false;
        }
        break;
      case 'LastMessageSource':
        if (user.LastMessageSource !== String(value)) {
          return false;
        }
        break;
      case 'IncomingLastMessageMediaType':
        if (user.IncomingLastMessageMediaType !== String(value)) {
          return false;
        }
        break;
      case 'IncomingLastMessageDateFrom':
        if (user.IncomingLastMessageDate && new Date(user.IncomingLastMessageDate) <= new Date(value)) {
          return false;
        }
        break;
      case 'IncomingLastMessageDateTo':
        if (user.IncomingLastMessageDate && new Date(user.IncomingLastMessageDate) >= new Date(value)) {
          return false;
        }
        break;
      case 'IncomingLastMessageIsImage':
        if (value === true && user.IncomingLastMessageMediaType !== 'image') {
          return false;
        }
        break;
      case 'IncomingLastMessageIsVideo':
        if (value === true && user.IncomingLastMessageMediaType !== 'video') {
          return false;
        }
        break;
      case 'IncomingLastMessageIsAudio':
        if (value === true && user.IncomingLastMessageMediaType !== 'audio') {
          return false;
        }
        break;
      case 'IncomingLastMessageSource':
        if (user.IncomingLastMessageSource !== String(value)) {
          return false;
        }
        break;
      case 'IncomingLastMessageContactUser':
        if (!Array.isArray(value)) {
          value = [value];
        }
        if (!value.includes(user.IncomingLastMessageContactUser)) {
          return false;
        }
        break;
      case 'OutcomingLastMessageMediaType':
        if (user.OutgoingLastMessageMediaType !== String(value)) {
          return false;
        }
        break;
      case 'OutcomingLastMessageDateFrom':
        if (user.OutcomingLastMessageDate && new Date(user.OutcomingLastMessageDate) <= new Date(value)) {
          return false;
        }
        break;
      case 'OutcomingLastMessageDateTo':
        if (user.OutcomingLastMessageDate && new Date(user.OutcomingLastMessageDate) >= new Date(value)) {
          return false;
        }
        break;
      case 'OutcomingLastMessageIsImage':
        if (value === true && user.OutgoingLastMessageMediaType !== 'image') {
          return false;
        }
        break;
      case 'OutcomingLastMessageIsVideo':
        if (value === true && user.OutgoingLastMessageMediaType !== 'video') {
          return false;
        }
        break;
      case 'OutcomingLastMessageIsAudio':
        if (value === true && user.OutgoingLastMessageMediaType !== 'audio') {
          return false;
        }
        break;
      case 'OutcomingLastMessageSource':
        if (user.OutgoingLastMessageSource !== String(value)) {
          return false;
        }
        break;
      default:
        break;
    }
  }

  return true;
}

const MESSAGE_PIPELINE_STATUS_RANK: Record<string, number> = {
  sending: 10,
  uploading: 10,
  'pending system': 20,
  'pending whatsapp': 20,
  validated_ready_to_send: 20,
  sent: 30,
  delivered: 40,
  read: 50,
};

const MESSAGE_PENDING_PIPELINE_STATUSES = new Set([
  'pending system',
  'pending whatsapp',
  'validated_ready_to_send',
]);

const MESSAGE_FAILURE_STATUSES = new Set(['error', 'send_failed', 'failed', 'not sent']);

function normalizeMessageStatusKey(status: MessageStatus): string {
  if (status == null) return '';
  return String(status).toLowerCase().trim();
}

function getMessageStatusRank(status: MessageStatus): number {
  return MESSAGE_PIPELINE_STATUS_RANK[normalizeMessageStatusKey(status)] ?? 0;
}

export const getMessageStatus = (newStatus: MessageStatus, oldStatus: MessageStatus): MessageStatus => {
  const newKey = normalizeMessageStatusKey(newStatus);
  const oldKey = normalizeMessageStatusKey(oldStatus);

  if (!newKey) return oldStatus ?? newStatus;
  if (!oldKey) return newStatus;

  if (MESSAGE_FAILURE_STATUSES.has(newKey)) return newStatus;

  const newRank = getMessageStatusRank(newStatus);
  const oldRank = getMessageStatusRank(oldStatus);

  if (newRank === 0 && oldRank === 0) return newStatus;
  if (newRank === 0) return oldStatus;
  if (oldRank === 0) return newStatus;

  // Never regress from confirmed send (sent+) to in-flight / pending pipeline states.
  if (oldRank >= MESSAGE_PIPELINE_STATUS_RANK.sent && newRank < oldRank) {
    return oldStatus;
  }

  // sending/uploading and pending pipeline states all render as a clock; keep the local
  // state so the HTTP response (not a socket echo) is what moves the message forward.
  if ((oldKey === 'sending' || oldKey === 'uploading') && MESSAGE_PENDING_PIPELINE_STATUSES.has(newKey)) {
    return oldStatus;
  }

  return newRank >= oldRank ? newStatus : oldStatus;
};
export const filterUnreadChatUsersByView = (
  unreadChatUsers: ChatUserModel[],
  userId: string | undefined,
  viewId: 'my-chats' | 'not-my-chats' | 'unassigned-chats' | 'bots-chats' | 'chats' | undefined,
) => {
  return unreadChatUsers.filter((user) => {
    switch (viewId) {
      case 'my-chats':
        return user.NotificationContactUser === userId;
      case 'not-my-chats':
        return user.NotificationContactUser && user.NotificationContactUser !== userId;
      case 'unassigned-chats':
        return user.NotificationContactUser === null || user.NotificationContactUser === undefined;
      case 'bots-chats':
        return user.isBot;
      case 'chats':
      default:
        return true;
    }
  });
};
//* Messages
export const messageTypesByFormat: {
  [key: string]: Array<MessageMediaType | null | undefined>;
} = {
  doc: ['document', 'docx', 'pdf', 'xlsx', 'zip'],
  image: ['image', 'gif', 'sticker'],
  video: ['video'],
};

export const getIsImageMessage = (
  fileType?: MessageMediaType | null,
  mediaNoteFormat?: MessageMediaType | null,
  file?: string | null,
) => {
  return Boolean(
    messageTypesByFormat.image.includes(fileType) ||
      messageTypesByFormat.image.includes(mediaNoteFormat) ||
      (file?.includes('data:image') ?? false) ||
      fileType?.match(/(png|jpe?g|gif)$/i),
  );
};
export const getIsVideoMessage = (
  fileType?: MessageMediaType | null,
  mediaNoteFormat?: MessageMediaType | null,
  file?: string | null,
) => {
  return (
    messageTypesByFormat.video.includes(fileType) ||
    messageTypesByFormat.video.includes(mediaNoteFormat) ||
    fileType?.match(/(mp4|webm|avi|mov|mkv|flv|wmv)$/i)
  );
};
export const getIsDocMessage = (
  fileType?: MessageMediaType | null,
  mediaNoteFormat?: MessageMediaType | null,
  file?: string | null,
) => {
  return (
    messageTypesByFormat.doc.includes(fileType) ||
    messageTypesByFormat.doc.includes(mediaNoteFormat) ||
    fileType === 'document' ||
    mediaNoteFormat === 'document'
  );
};
export const getIsAudioMessage = (
  fileType?: MessageMediaType | null,
  mediaNoteFormat?: MessageMediaType | null,
) => {
  return (
    fileType === 'audio' ||
    mediaNoteFormat === 'audio' ||
    audioExtensionRegex.test(fileType as string) ||
    audioExtensionRegex.test(mediaNoteFormat as string)
  );
};

//* Templates
export const checkIsTemplateValidWithVarsData = (varsData: TemplateVarsData, skipMedia?: boolean) => {
  let result: {
    valid: boolean;
    details: {
      [keyof in TemplateVarsData[0]['name']]?: boolean;
    };
  } = {
    valid: false,
    details: {},
  };

  try {
    const errorsObj: {
      [keyof in TemplateVarsData[0]['name']]?: boolean;
    } = {};
    varsData.forEach((param) => {
      errorsObj[param.name] = param.name === 'data' && skipMedia ? false : param.text === '';
    });

    result = {
      valid: !Object.values(errorsObj).includes(true),
      details: errorsObj,
    };
  } catch (error) {
    console.warn('checkIsTemplateValid error: ', error);
  }

  return result;
};

//* message preview
export const fileTypes = ['pdf', 'xlsx', 'xls', 'docx', 'doc', 'pdf'];
export const iconStyle = { width: 14, height: 14, color: 'grey.800' };
const outgoingFailedIconStyle = { width: 18, height: 18, color: 'error.main' };
const outgoingSendFailedIconStyle = { width: 18, height: 18, color: 'warning.main' };

export function getOutgoingLastMessageStatus(user: ChatUserModel): string | null {
  return user.OutcomingLastMessageStatus ?? user.LastMessageSource ?? null;
}

/** הודעה יוצאת אחרונה שלא נשלחה / נכשלה (לא כולל ממתין לשליחה) */
export function isOutgoingLastMessageSendFailed(user: ChatUserModel): boolean {
  if (user.LastMessageType !== 'outcoming') return false;
  const status = getOutgoingLastMessageStatus(user);
  if (!status) return false;
  return isOutgoingLastMessageFailure(status);
}
export const mediaTypeIconMap: Record<string, React.ElementType> = {
  location: LocationOnIcon,
  audio: MicIcon,
  image: ImageIcon,
  contacts: PersonIcon,
  template: StickyNote2Icon,
  video: VideoCameraBackIcon,
  zip: FolderZipIcon,
};
export const getMediaIcon = (mediaType?: string | null) => {
  if (!mediaType) return null;

  const lowerMediaType = mediaType.toLowerCase();
  // If type exists – return existing icon
  if (mediaTypeIconMap[lowerMediaType]) {
    const IconComponent = mediaTypeIconMap[lowerMediaType];
    return <IconComponent sx={iconStyle} />;
  }
  if (fileTypes.includes(lowerMediaType)) {
    return <DescriptionIcon sx={iconStyle} />;
  }
  return null;
};

const outgoingListOkSet = new Set<string>(OUTGOING_NON_FAILURE_STATUSES);

export const getOutcomingIcon = (status?: string | null) => {
  if (!status) return null;

  const statusLower = status.toLowerCase().trim();

  if (statusLower === 'send_failed') {
    return <ErrorOutlineIcon sx={outgoingSendFailedIconStyle} />;
  }

  if (outgoingListOkSet.has(statusLower)) {
    switch (statusLower) {
      case 'sent':
        return <DoneIcon sx={iconStyle} />;
      case 'delivered':
        return <DoneAllIcon sx={iconStyle} />;
      case 'read':
        return <DoneAllIcon sx={{ ...iconStyle, color: 'secondary.main' }} />;
      default:
        return null;
    }
  }

  return <ErrorOutlineIcon sx={outgoingFailedIconStyle} />;
};
export const getMessagePreviewText = (user: ChatUserModel, intl?: IntlShape) => {
  if (user.LastMessageMediaType === 'template' && IsJsonString(user.LastMessage)) {
    const parsed = safeParseJSON(user.LastMessage);
    return parsed?.['BODY']?.text || '';
  }
  if (user.LastMessage === 'image') return (intl || intlGlobal).formatMessage({ id: 'chat.messages.image' });
  if (user.LastMessage === 'buttons')
    return (intl || intlGlobal).formatMessage({ id: 'chat.messages.buttons' });
  return user.LastMessage || '';
};
export const getSingleMessagePreviewText = (
  mediaType: MessageMediaType,
  message: string,
  intl?: IntlShape,
) => {
  if (mediaType === 'template' && IsJsonString(message)) {
    const parsed = safeParseJSON(message);
    return parsed?.['BODY']?.text || '';
  }
  if (mediaType === 'image' && message === '')
    return (intl || intlGlobal).formatMessage({ id: 'chat.messages.image' });
  if (mediaType === 'buttons' && message === '')
    return (intl || intlGlobal).formatMessage({ id: 'chat.messages.buttons' });
  return message || '';
};
